import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
  EntityState,
  nanoid,
  PayloadAction,
} from "@reduxjs/toolkit";
import {
  DateRange,
  DeletePaymentProhibition,
  GarnishmentRestriction,
  getTalentPaymentProhibitions,
  postCreatePaymentProhibition,
  postDeletePaymentProhibition,
  postUpdatePaymentProhibition,
  postUpdatePaymentProhibitionCancelDetails,
  TalentPaymentProhibition,
  TalentPaymentProhibitionCancelDetails,
} from "./paymentProhibitionAPI";
import { RootState } from "../../../../app/store";

// Adapter for managing TalentPaymentProhibition items
const paymentProhibitionsAdapter = createEntityAdapter<
  TalentPaymentProhibition,
  TalentPaymentProhibition["id"]
>({
  selectId: (paymentProhibition) => paymentProhibition.id,
});

export type SliceState = EntityState<
  TalentPaymentProhibition,
  TalentPaymentProhibition["id"]
> & {
  status: "idle" | "pending" | "succeeded" | "rejected";
  error: string | null;
};

// Initial state using adapter's getInitialState
const initialState: SliceState = paymentProhibitionsAdapter.getInitialState({
  status: "idle",
  error: null,
});

export const fetchTalentPaymentProhibitions = createAsyncThunk<
  any,
  string,
  { state: RootState }
>(
  "talentPaymentProhibitions/fetchTalentPaymentProhibitions",
  async (talentId: string, { requestId }) => {
    const res = await getTalentPaymentProhibitions(talentId, { requestId });
    return res.data;
  },
);

export const createTalentPaymentProhibition = createAsyncThunk<
  { data: any; id: string },
  TalentPaymentProhibition,
  { state: RootState }
>(
  "talentPaymentProhibitions/createTalentPaymentProhibition",
  async (paymentProhibition: TalentPaymentProhibition, { requestId }) => {
    const res = await postCreatePaymentProhibition(paymentProhibition, {
      requestId,
    });
    return { data: res.data, id: paymentProhibition.id };
  },
);

export const updateTalentPaymentProhibition = createAsyncThunk<
  { data: TalentPaymentProhibition; id: string },
  TalentPaymentProhibition,
  { state: RootState }
>(
  "talentPaymentProhibitions/updateTalentPaymentProhibition",
  async (paymentProhibition: TalentPaymentProhibition, { requestId }) => {
    const response = await postUpdatePaymentProhibition(paymentProhibition, {
      requestId,
    });
    return { data: response.data, id: paymentProhibition.id };
  },
);

export const updateTalentPaymentProhibitionCancelDetails = createAsyncThunk<
  { data: TalentPaymentProhibitionCancelDetails; id: string },
  TalentPaymentProhibitionCancelDetails,
  { state: RootState }
>(
  "talentPaymentProhibitions/updateTalentPaymentProhibitionCancelDetails",
  async (
    paymentProhibition: TalentPaymentProhibitionCancelDetails,
    { requestId },
  ) => {
    const response = await postUpdatePaymentProhibitionCancelDetails(
      paymentProhibition,
      { requestId },
    );
    return { data: response.data, id: paymentProhibition.id };
  },
);

export const deleteTalentPaymentProhibition = createAsyncThunk<
  DeletePaymentProhibition,
  any,
  { state: RootState }
>(
  "talentPaymentProhibitions/deleteTalentPaymentProhibition",
  async (deletePaymentProhibition: DeletePaymentProhibition, { requestId }) => {
    const response = await postDeletePaymentProhibition(
      deletePaymentProhibition,
      { requestId },
    );
    return response.data;
  },
);

// Format payment prohibitions so that it replaces
// dots with commas
const formatPaymentProhibition = (
  paymentProhibition: Partial<TalentPaymentProhibition>,
) => {
  const formattedPaymentProhibition: Partial<TalentPaymentProhibition> = {
    ...paymentProhibition,
    // Check and include properties only if they are updated
    dailyProtectedPortion: paymentProhibition.dailyProtectedPortion
      ? paymentProhibition.dailyProtectedPortion.replace(".", ",")
      : paymentProhibition.dailyProtectedPortion,
    additionalDeduction: paymentProhibition.additionalDeduction
      ? paymentProhibition.additionalDeduction.replace(".", ",")
      : paymentProhibition.additionalDeduction,
    externalMonthlyNetIncome: paymentProhibition.externalMonthlyNetIncome
      ? paymentProhibition.externalMonthlyNetIncome.replace(".", ",")
      : paymentProhibition.externalMonthlyNetIncome,
    deductionTypeAmount: paymentProhibition.deductionTypeAmount
      ? paymentProhibition.deductionTypeAmount.replace(".", ",")
      : paymentProhibition.deductionTypeAmount,
    // Map over garnishment restrictions and include percentage if it's updated
    garnishmentRestrictions: paymentProhibition.garnishmentRestrictions
      ? paymentProhibition.garnishmentRestrictions.map(
          (gRest: GarnishmentRestriction) => ({
            ...gRest,
            amount: gRest.amount
              ? gRest.amount.replace(".", ",")
              : gRest.amount,
          }),
        )
      : paymentProhibition.garnishmentRestrictions,
  };
  return formattedPaymentProhibition;
};

type ExtendedTalentPaymentProhibition = Partial<TalentPaymentProhibition> & {
  [key: string]: any;
};

// Define a mapping of event names to their corresponding properties
const eventToNullPropertyMap: Record<string, string> = {
  talPayProhibDailyProtPortUpd: "dailyProtectedPortion",
  talPayProhibAddDedUpd: "additionalDeduction",
  talPayProhibStartUpd: "start",
  talPayProhibEndUpd: "end",
  talPayProhibExtMonNetIncUpd: "externalMonthlyNetIncome",
  talPayProhibGarnRestUpd: "garnishmentRestrictions",
  talPayProhibEnfAgcyUpd: "enforcementAgency",
  talPayProhibRfRefUpd: "rfReference",
  talPayProhibFreeMonUpd: "freeMonths",
  talPayProhibIbanUpd: "iban",
  talPayProhibBicUpd: "bic",
  talPayProhibEmplUpd: "employer",
  talPayProhibDedTypeUpd: "deductionType",
  talPayProhibDedTypeAmtUpd: "deductionTypeAmount",
};

// Utility function to process event payloads
function processPaymentProhibitionEvents(
  events: any,
  existingProhibition: Partial<TalentPaymentProhibition>,
) {
  let updatedProhibition: ExtendedTalentPaymentProhibition = {
    ...existingProhibition,
  };

  events.forEach((event: any) => {
    switch (event.name) {
      case "talPayProhibNew":
        updatedProhibition = {
          ...updatedProhibition,
          ...event.payload,
          id: event.id,
          version: event.version,
        };
        break;
      case "talPayProhibStartUpd":
      case "talPayProhibEndUpd":
      case "talPayProhibDailyProtPortUpd":
      case "talPayProhibExtMonNetIncUpd":
      case "talPayProhibGarnRestUpd":
      case "talPayProhibEnfAgcyUpd":
      case "talPayProhibRfRefUpd":
      case "talPayProhibFreeMonUpd":
      case "talPayProhibIbanUpd":
      case "talPayProhibBicUpd":
      case "talPayProhibAddDedUpd":
      case "talPayProhibEmplUpd":
      case "talPayProhibDedTypeUpd":
      case "talPayProhibDedTypeAmtUpd":
        // Check if event is higher version
        const eventIsHigherVersion =
          event.version > (updatedProhibition.version || 0);
        // Check if event id matches updatedProhibition id
        let idMatches = event.id === updatedProhibition.id;

        // If id is not found, give it to updatedProhibition from event.id (only once)
        if (!idMatches) {
          updatedProhibition = { ...updatedProhibition, id: event.id };
          idMatches = true;
        }

        if (eventIsHigherVersion && idMatches) {
          // If event payload is not empty, update with payload
          if (Object.keys(event.payload).length > 0) {
            updatedProhibition = {
              ...updatedProhibition,
              ...event.payload,
              version: event.version,
            };
            // If event payload is empty, update with empty value
          } else if (Object.keys(eventToNullPropertyMap).includes(event.name)) {
            // Nullify if property is missing in event payload
            const property = eventToNullPropertyMap[event.name];
            updatedProhibition = {
              ...updatedProhibition,
              [property]: null,
              version: event.version,
            };
          }
          if (
            event.name === "talPayProhibGarnRestUpd" ||
            event.name === "talPayProhibFreeMonUpd"
          ) {
            const key =
              event.name === "talPayProhibGarnRestUpd"
                ? "garnishmentRestrictions"
                : "freeMonths";
            if (event.payload && event.payload[key]) {
              updatedProhibition[key] = event.payload[key].map(
                (item: DateRange | GarnishmentRestriction) => ({
                  ...item,
                  id: nanoid(),
                }),
              );
            }
          }
        }
        break;
      default:
        break;
    }
  });

  const formattedUpdatedProhibition =
    formatPaymentProhibition(updatedProhibition);

  return formattedUpdatedProhibition;
}

export const paymentProhibitionsSlice = createSlice({
  name: "talentPaymentProhibitions",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(
      fetchTalentPaymentProhibitions.fulfilled,
      (state, action: PayloadAction<TalentPaymentProhibition[]>) => {
        // Enrichen freeMonths and garnishmentRestrictions with ids
        action.payload.forEach(
          (paymentProhibition: TalentPaymentProhibition) => {
            // Check if freeMonths is defined before iterating
            if (paymentProhibition.freeMonths) {
              paymentProhibition.freeMonths.forEach((month: DateRange) => {
                month.id = nanoid();
              });
            }
            // Check if garnishmentRestrictions is defined before iterating
            if (paymentProhibition.garnishmentRestrictions) {
              paymentProhibition.garnishmentRestrictions.forEach(
                (restriction: GarnishmentRestriction) => {
                  restriction.id = nanoid();
                },
              );
            }
          },
        );

        const formattedPaymentProhibitions = action.payload.map(
          (paymentProhibition) => formatPaymentProhibition(paymentProhibition),
        ) as TalentPaymentProhibition[];

        paymentProhibitionsAdapter.setAll(state, formattedPaymentProhibitions);
        state.status = "succeeded";
      },
    );
    builder.addCase(
      createTalentPaymentProhibition.fulfilled,
      (state, action: PayloadAction<any>) => {
        let newPaymentProhibition: Partial<TalentPaymentProhibition> = {
          id: action.payload.id,
        };

        const updatedProhibition = processPaymentProhibitionEvents(
          action.payload.data,
          newPaymentProhibition,
        );
        updatedProhibition.id &&
          paymentProhibitionsAdapter.addOne(
            state,
            updatedProhibition as TalentPaymentProhibition,
          );
        state.status = "succeeded";
      },
    );
    builder.addCase(
      updateTalentPaymentProhibition.fulfilled,
      (state, action: PayloadAction<any>) => {
        // Get the paymentProhibitionItem from the state
        const paymentProhibitionItem = state.entities[action.payload.id];

        // Create a new object with the changes
        let paymentProhibitionChanges: Partial<TalentPaymentProhibition> = {
          ...paymentProhibitionItem,
        };
        paymentProhibitionChanges = processPaymentProhibitionEvents(
          action.payload.data,
          paymentProhibitionChanges,
        );
        paymentProhibitionChanges.id &&
          paymentProhibitionsAdapter.updateOne(state, {
            id: paymentProhibitionChanges.id,
            changes: paymentProhibitionChanges,
          });
        state.status = "succeeded";
      },
    );
    builder.addCase(
      updateTalentPaymentProhibitionCancelDetails.fulfilled,
      (state, action: PayloadAction<any>) => {
        // Get the paymentProhibitionItem from the state
        const paymentProhibitionItem = state.entities[action.payload.id];

        // Create a new object with the changes
        let paymentProhibitionChanges: Partial<TalentPaymentProhibition> = {
          ...paymentProhibitionItem,
        };
        paymentProhibitionChanges = processPaymentProhibitionEvents(
          action.payload.data,
          paymentProhibitionChanges,
        );
        paymentProhibitionChanges.id &&
          paymentProhibitionsAdapter.updateOne(state, {
            id: paymentProhibitionChanges.id,
            changes: paymentProhibitionChanges,
          });
        state.status = "succeeded";
      },
    );

    builder.addCase(
      deleteTalentPaymentProhibition.fulfilled,
      (state, action: PayloadAction<any>) => {
        action.payload.forEach((event: any) => {
          if (event.name === "talPayProhibDel") {
            paymentProhibitionsAdapter.removeOne(state, event.id);
          }
        });
        state.status = "succeeded";
      },
    );
    builder.addMatcher(
      (action) =>
        action.type.startsWith("talentPaymentProhibitions/") &&
        action.type.endsWith("/pending"),
      (state) => {
        state.status = "pending";
      },
    );
    builder.addMatcher(
      (action) => action.type.endsWith("/rejected"),
      (state) => {
        state.status = "rejected";
      },
    );
  },
});

// Corrected selectors
export const {
  selectAll: selectAllPaymentProhibitions,
  selectById: selectPaymentProhibitionById,
} = paymentProhibitionsAdapter.getSelectors<RootState>(
  (state) => state.paymentProhibitions,
);

export const paymentProhibitionsReducer = paymentProhibitionsSlice.reducer;
